// src/hooks/useAttachments.ts
//
// Manages a list of attachments where each one uploads independently
// of the others. Each attachment has its own status, progress, error
// state, and storage path once complete.
//
// Modeled on how ChatGPT / Claude's web composer works: you drop
// multiple files, each tile shows its own progress, and you can
// remove or retry individual ones before sending.
//
// The `imagePath` field is populated only when an attachment finishes
// uploading. The UI gates "send" on "≥1 attachment with imagePath
// AND none in 'preparing'/'uploading'/'retrying' state."

import { useCallback, useRef, useState } from "react";
import {
  uploadImageToStorage,
  UploadProgress,
} from "../utils/imageUpload";

export type AttachmentStatus =
  | "preparing"
  | "uploading"
  | "retrying"
  | "complete"
  | "error";

export interface Attachment {
  /** Stable client-side id (for React keys + remove/retry lookups) */
  id: string;
  /** A blob/object URL preview the UI can render before/while uploading */
  previewUrl: string;
  /** The file we're uploading (kept for retry) */
  file: File;
  status: AttachmentStatus;
  progress: number;
  /** Storage path, populated once status === 'complete' */
  imagePath?: string;
  /** Error message when status === 'error' */
  error?: string;
}

export interface UseAttachmentsResult {
  attachments: Attachment[];
  /** True when at least one is in flight (not complete or errored) */
  isAnyInFlight: boolean;
  /** True when there's at least one ready-to-send attachment */
  hasCompleted: boolean;
  /** Storage paths for all completed attachments, in attachment order */
  completedPaths: string[];

  add: (files: File[] | FileList) => void;
  /** Insert an already-uploaded image (e.g. from the camera flow). */
  addCompleted: (imagePath: string, previewUrl: string) => void;
  remove: (id: string) => void;
  retry: (id: string) => void;
  clear: () => void;
}

const MAX_ATTACHMENTS = 6;

const newId = () =>
  `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function useAttachments(conversationId: string): UseAttachmentsResult {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // Track preview URLs so we can revoke them on remove/clear
  const previewUrlsRef = useRef<Map<string, string>>(new Map());

  const updateOne = useCallback(
    (id: string, patch: Partial<Attachment>) => {
      setAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
      );
    },
    []
  );

  const startUpload = useCallback(
    (id: string, file: File) => {
      const handleProgress = (p: UploadProgress) => {
        updateOne(id, {
          status:
            p.status === "complete"
              ? "complete"
              : p.status === "error"
              ? "error"
              : (p.status as AttachmentStatus),
          progress: p.progress,
          error: p.error,
        });
      };

      uploadImageToStorage(file, conversationId, {
        maxRetries: 3,
        retryDelay: 1000,
        onProgress: handleProgress,
      })
        .then((path: string) => {
          updateOne(id, {
            status: "complete",
            progress: 100,
            imagePath: path,
            error: undefined,
          });
        })
        .catch((err: any) => {
          updateOne(id, {
            status: "error",
            error: err?.message || "Upload failed",
          });
        });
    },
    [conversationId, updateOne]
  );

  const add = useCallback(
    (input: File[] | FileList) => {
      const files = Array.from(input).filter((f) => f.type.startsWith("image/"));
      if (files.length === 0) return;

      setAttachments((prev) => {
        const room = MAX_ATTACHMENTS - prev.length;
        if (room <= 0) {
          console.warn(
            `Attachment limit (${MAX_ATTACHMENTS}) reached, ignoring ${files.length} file(s)`
          );
          return prev;
        }
        const accepted = files.slice(0, room);

        const newAttachments: Attachment[] = accepted.map((file) => {
          const id = newId();
          const previewUrl = URL.createObjectURL(file);
          previewUrlsRef.current.set(id, previewUrl);
          return {
            id,
            previewUrl,
            file,
            status: "preparing",
            progress: 0,
          };
        });

        // Kick off uploads asynchronously after state commits — we
        // need each id committed first so the progress callbacks can
        // update the right row.
        queueMicrotask(() => {
          newAttachments.forEach((a) => startUpload(a.id, a.file));
        });

        return [...prev, ...newAttachments];
      });
    },
    [startUpload]
  );

  const remove = useCallback((id: string) => {
    const url = previewUrlsRef.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      previewUrlsRef.current.delete(id);
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    // NOTE: We don't actually cancel the in-flight upload — Firebase
    // SDK doesn't expose AbortSignal for uploadBytes. The upload will
    // complete in the background and the result is discarded since
    // the entry is already gone from state. The Storage file will
    // exist briefly until cleanup; not worth the complexity here.
  }, []);

  const retry = useCallback(
    (id: string) => {
      setAttachments((prev) => {
        const target = prev.find((a) => a.id === id);
        if (!target) return prev;
        return prev.map((a) =>
          a.id === id
            ? { ...a, status: "preparing", progress: 0, error: undefined }
            : a
        );
      });
      // Find the file from current state and retry
      setAttachments((prev) => {
        const target = prev.find((a) => a.id === id);
        if (target) {
          queueMicrotask(() => startUpload(id, target.file));
        }
        return prev;
      });
    },
    [startUpload]
  );

  const clear = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    setAttachments([]);
  }, []);

  /**
   * Insert an already-uploaded image as a completed attachment.
   * Used by the camera flow which does its own upload (with its own
   * progress UI inside the camera modal). The path is the storage
   * path returned by uploadImageToStorage. `previewUrl` should be a
   * blob: URL or a base64 data URL so the strip can show a thumbnail
   * immediately without re-fetching from Storage.
   */
  const addCompleted = useCallback(
    (imagePath: string, previewUrl: string) => {
      setAttachments((prev) => {
        if (prev.length >= MAX_ATTACHMENTS) return prev;
        const id = newId();
        // We don't own this previewUrl's lifecycle (camera created
        // it from the captured blob), but we do still revoke on
        // remove/clear to be safe — revoking a URL we didn't create
        // is a no-op.
        previewUrlsRef.current.set(id, previewUrl);
        return [
          ...prev,
          {
            id,
            previewUrl,
            // Synthetic empty file — only used for retry, which
            // doesn't apply to already-completed attachments.
            file: new File([], "captured.jpg", { type: "image/jpeg" }),
            status: "complete" as AttachmentStatus,
            progress: 100,
            imagePath,
          },
        ];
      });
    },
    []
  );

  const isAnyInFlight = attachments.some(
    (a) =>
      a.status === "preparing" ||
      a.status === "uploading" ||
      a.status === "retrying"
  );

  const completedPaths = attachments
    .filter((a) => a.status === "complete" && a.imagePath)
    .map((a) => a.imagePath as string);

  const hasCompleted = completedPaths.length > 0;

  return {
    attachments,
    isAnyInFlight,
    hasCompleted,
    completedPaths,
    add,
    addCompleted,
    remove,
    retry,
    clear,
  };
}
